import { supabase, getCurrentUser } from './supabase';
import type { JobCv } from '../types';

const BUCKET = 'job-cvs';
const TABLE = 'job_cvs';

export async function uploadCv(file: File, name: string): Promise<JobCv> {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}.${fileExt}`;
  const filePath = `${user.id}/${fileName}`;

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(filePath, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (uploadErr) throw uploadErr;

  const { data, error } = await supabase
    .from(TABLE)
    .insert({ user_id: user.id, name, file_path: filePath, is_default: false })
    .select()
    .single();
  if (error) throw error;
  return data as JobCv;
}

export async function fetchCvs(): Promise<JobCv[]> {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  if (error) throw error;

  const cvs = await Promise.all(
    (data || []).map(async (cv: any) => {
      const { data: signedData } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(cv.file_path, 60 * 60);
      return { ...cv, url: signedData?.signedUrl } as JobCv;
    })
  );
  return cvs;
}

export async function setDefaultCv(id: string): Promise<void> {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  await supabase
    .from(TABLE)
    .update({ is_default: false })
    .eq('user_id', user.id);

  const { error } = await supabase
    .from(TABLE)
    .update({ is_default: true })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteCv(id: string, filePath: string): Promise<void> {
  await supabase.storage.from(BUCKET).remove([filePath]);
  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  if (error) throw error;
}
